import { useMemo } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { ExportButton } from '../../../shared/components/ExportButton';
import { useEmailCampaignLogs } from '../hooks/useEmailCampaigns';
import { logStatusTone, summariseLogs } from '../recipients';
import type { EmailCampaign } from '../types';

const TONE_CLASSES: Record<'success' | 'error' | 'neutral', string> = {
  success: 'bg-green-500/10 text-green-600',
  error: 'bg-red-500/10 text-red-600',
  neutral: 'bg-muted text-muted-foreground',
};

interface CampaignLogsModalProps {
  campaign: EmailCampaign | null;
  onClose: () => void;
}

/**
 * Per-recipient delivery detail for one campaign (PE-2125).
 *
 * There is no `GET /email-campaigns/:id`, so everything above the log table comes
 * from the list row the user clicked — don't add a detail fetch for a route the
 * BFF doesn't expose. Aggregate stats (open rate etc.) have no source at all in
 * PE-891; the counts here are derived from the delivery statuses themselves,
 * which is genuinely all that exists. Real stats are PE-2204.
 */
export function CampaignLogsModal({ campaign, onClose }: CampaignLogsModalProps) {
  const { data: logs = [], isLoading, error, isFetching, refetch } = useEmailCampaignLogs(
    campaign?.id ?? null,
  );

  const stats = useMemo(() => summariseLogs(logs), [logs]);

  if (!campaign) return null;

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-[20px] font-bold text-foreground">{campaign.title}</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {campaign.recipientCount} recipient{campaign.recipientCount === 1 ? '' : 's'} ·
              template <span className="text-foreground">{campaign.templateId}</span> ·{' '}
              {new Date(campaign.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            <ExportButton
              endpoint={`/email-campaigns/${campaign.id}/logs/export`}
              filenameBase={`email-campaign-${campaign.id}-logs`}
              label="Export logs"
            />
          </div>
        </div>

        {/* ---- Derived delivery counts ---- */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Logged', value: stats.total },
            { label: 'Delivered', value: stats.delivered },
            { label: 'Failed', value: stats.failed },
            { label: 'Other', value: stats.other },
          ].map((tile) => (
            <div key={tile.label} className="bg-card rounded-2xl border border-border p-4">
              <p className="text-[12px] text-muted-foreground">{tile.label}</p>
              <p className="mt-1 text-[20px] font-semibold text-foreground tabular-nums">
                {tile.value}
              </p>
            </div>
          ))}
        </div>

        {/* ---- Per-recipient log ---- */}
        <div className="mt-6">
          {isLoading ? (
            <p className="text-[14px] text-muted-foreground py-8 text-center">Loading logs…</p>
          ) : error ? (
            <p className="text-[14px] text-red-500 py-8 text-center">
              Could not load delivery logs.
            </p>
          ) : logs.length === 0 ? (
            <p className="text-[14px] text-muted-foreground py-8 text-center">
              No delivery logs recorded yet. Sending is asynchronous — try Refresh in a moment.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 font-[600] text-foreground">Recipient</th>
                    <th className="px-4 py-3 font-[600] text-foreground">Status</th>
                    <th className="px-4 py-3 font-[600] text-foreground">Detail</th>
                    <th className="px-4 py-3 font-[600] text-foreground">Logged</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const tone = logStatusTone(log.status);
                    return (
                      <tr key={log.id} className="border-t border-border">
                        <td className="px-4 py-3 text-foreground">{log.recipientEmail}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block rounded-full px-2.5 py-1 text-[12px] ${TONE_CLASSES[tone]}`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {log.error ?? log.messageId ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
